import { useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { GoogleMapsWarmup } from './GoogleMapsWarmup';
import { PushNotificationBanner } from '@/components/notifications/PushNotificationBanner';
import { PendingInvitationsBanner } from '@/components/invites/PendingInvitationsBanner';
import { DashboardSectionNavigation } from './DashboardSectionNavigation';
import { TrialLifecycleBanner } from '@/components/billing/TrialLifecycleBanner';
import { SubscriptionWarningDialog } from '@/components/billing/SubscriptionWarningDialog';
import { useDashboardScrollRestoration } from '@/hooks/useDashboardScrollRestoration';

export function DashboardLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainScrollRef = useRef<HTMLElement>(null);

  useDashboardScrollRestoration(mainScrollRef);

  return (
    <div className="flex h-screen min-h-[100dvh] w-full overflow-hidden bg-bg text-foreground">
      {/* Desktop sidebar */}
      <div className="hidden shrink-0 lg:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-72 animate-slide-in-left lg:hidden">
            <Sidebar
              collapsed={false}
              onToggle={() => setMobileOpen(false)}
              onMobileClose={() => setMobileOpen(false)}
            />
          </div>
        </>
      )}

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar onMenuClick={() => setMobileOpen(!mobileOpen)} />
        <GoogleMapsWarmup />
        <PushNotificationBanner />
        <PendingInvitationsBanner />
        <TrialLifecycleBanner />
        <SubscriptionWarningDialog />
        <main
          ref={mainScrollRef}
          data-dashboard-scroll-container
          className="scrollbar-thin min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6 lg:px-8 lg:py-7 xl:px-10"
        >
          <DashboardSectionNavigation />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
